// Shared labels / status rules of the single-elimination bracket (view, panel, public page).

/** "Final", "Semifinal", "Quarterfinal", "Round of 16", "3rd place" from the server's round_name. */
export function roundLabel(t, rn, round) {
  if (!rn) return t('bracket.round.n', 'Round {{n}}', { n: round });
  switch (rn.key) {
    case 'final': return t('bracket.round.final', 'Final');
    case 'semifinal': return t('bracket.round.semifinal', 'Semifinal');
    case 'quarterfinal': return t('bracket.round.quarterfinal', 'Quarterfinal');
    case 'third_place': return t('bracket.round.thirdPlace', '3rd place');
    case 'round_of': return t('bracket.round.roundOf', 'Round of {{n}}', { n: rn.teams });
    case 'ub_round': return t('bracket.round.ubRound', 'Upper round {{n}}', { n: rn.n });
    case 'ub_semifinal': return t('bracket.round.ubSemifinal', 'Upper semifinal');
    case 'ub_final': return t('bracket.round.ubFinal', 'Upper final');
    case 'lb_round': return t('bracket.round.lbRound', 'Lower round {{n}}', { n: rn.n });
    case 'lb_final': return t('bracket.round.lbFinal', 'Lower final');
    case 'grand_final': return t('bracket.round.grandFinal', 'Grand final');
    case 'grand_final_reset': return t('bracket.round.grandFinalReset', 'Grand final reset');
    case 'swiss_round': return t('bracket.round.n', 'Round {{n}}', { n: rn.n });
    case 'matchday': return t('bracket.round.matchday', 'Matchday {{n}}', { n: rn.n });
    case 'gsl_opening': return t('bracket.gsl.openingMatch', 'Opening match');
    case 'gsl_winners': return t('bracket.gsl.winners', "Winners' match");
    case 'gsl_elimination': return t('bracket.gsl.elimination', 'Elimination match');
    case 'gsl_decider': return t('bracket.gsl.decider', 'Decider match');
    case 'gsl_final': return t('bracket.gsl.final', 'Group final');
    default: return t('bracket.round.n', 'Round {{n}}', { n: round });
  }
}

/** One status per card: bye | walkover | done | decision | live | scheduled | ready | waiting. */
export function cardStatus(m) {
  if (m.status === 'completed' && m.walkover) {
    if (m.bracket_side === 'grand_final' && m.round_number === 2) return 'not_needed'; // UB team won the grand final
    const teams = (m.team1_id ? 1 : 0) + (m.team2_id ? 1 : 0);
    // a group match with one team (GSL group of 3: the missing seed, then the elimination match it empties) = a bye
    if (String(m.bracket_side || '').startsWith('group_') && teams < 2) return 'bye';
    return m.round_number === 1 && !m.is_third_place && (m.bracket_side || 'winners') === 'winners' && teams < 2 ? 'bye' : 'walkover';
  }
  if (m.status === 'completed' && m.is_draw) return 'draw';
  if (m.status === 'completed') return 'done';
  if (m.needs_decision) return 'decision';
  if (m.live || m.match_status === 'live') return 'live';
  if (m.match_id || m.status === 'scheduled') return 'scheduled';
  if (m.team1_id && m.team2_id) return 'ready';
  return 'waiting';
}

export function statusLabel(t, st) {
  return {
    bye: t('bracket.status.bye', 'Bye'),
    walkover: t('bracket.status.walkover', 'Walkover'),
    not_needed: t('bracket.status.notNeeded', 'Not needed'),
    draw: t('bracket.status.draw', 'Draw'),
    done: t('bracket.status.done', 'Done'),
    decision: t('bracket.status.decision', 'Needs decision'),
    live: t('bracket.status.live', 'Live'),
    scheduled: t('bracket.status.scheduled', 'Scheduled'),
    ready: t('bracket.status.ready', 'Ready'),
    waiting: t('bracket.status.waiting', 'Waiting'),
  }[st] || st;
}

/** Scores to show per side: map wins for BO3/BO5, round score for BO1. null = nothing to show. */
export function scoresOf(m) {
  if (!m.match_id || m.match_status == null || m.match_status === 'pending') return null;
  const bo = m.match_best_of || m.best_of || 1;
  if (bo > 1) return [m.match_map_wins_team1 ?? 0, m.match_map_wins_team2 ?? 0];
  return [m.match_score_team1 ?? 0, m.match_score_team2 ?? 0];
}

/** "Winner of R1 M2" / "Loser of R2 M1" for an empty slot, from the feeder links. */
export function slotSource(t, matches, m, slot) {
  const f = matches.find(x => (x.winner_to_id === m.id && x.winner_to_slot === slot) || (x.loser_to_id === m.id && x.loser_to_slot === slot));
  if (!f) return t('bracket.tbd', 'TBD');
  return f.winner_to_id === m.id && f.winner_to_slot === slot
    ? t('bracket.winnerOf', 'Winner of {{code}}', { code: f.code })
    : t('bracket.loserOf', 'Loser of {{code}}', { code: f.code });
}

export const teamName = (teams, id) => (id && teams[id] ? (teams[id].name || teams[id].tag) : null);

/** Playoff preview of a two-stage bracket: placeholder teams ("Group A #1", "Swiss #3") in the panel language. */
export function placeholderTeams(t, teams) {
  const out = {};
  for (const [id, tm] of Object.entries(teams || {})) {
    const p = tm.placeholder;
    out[id] = !p ? tm : { ...tm, name: p.rank
      ? t('bracket.po.swissRank', 'Swiss #{{n}}', { n: p.rank })
      : t('bracket.po.groupPlace', 'Group {{group}} #{{n}}', { group: p.group, n: p.place }) };
  }
  return out;
}
